/**
 * Frontend plan feature map — mirrors backend/src/utils/plans.ts.
 * Controls which modules are unlocked per plan.
 */

export const PLAN_FEATURES = {
  DASHBOARD: 'dashboard',
  ONBOARDING: 'onboarding',
  CUSTOMERS: 'customers',
  ORDERS: 'orders',
  SALE: 'sale',
  REPAIRS: 'repairs',
  OLD_GOLD: 'old-gold',
  SEGMENTS: 'segments',
  REPORTS: 'reports',
  ANALYTICS: 'analytics',
  PRIORITY_SUPPORT: 'priority-support',
} as const;

/** Human labels for package feature ids (used on the pricing page). */
export const FEATURE_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  onboarding: 'Customer onboarding',
  customers: 'Customer management',
  orders: 'Order tracking',
  sale: 'New sale & billing',
  repairs: 'Repair tracking',
  'old-gold': 'Old gold exchange',
  segments: 'Customer segments',
  reports: 'Reports',
  analytics: 'Analytics',
  'priority-support': 'Priority support',
};

const PLAN_MODULES: Record<string, string[]> = {
  // Demo preview unlocks everything so customers can explore the full product
  FREE: [
    PLAN_FEATURES.DASHBOARD,
    PLAN_FEATURES.ONBOARDING,
    PLAN_FEATURES.CUSTOMERS,
    PLAN_FEATURES.ORDERS,
    PLAN_FEATURES.SALE,
    PLAN_FEATURES.REPAIRS,
    PLAN_FEATURES.OLD_GOLD,
    PLAN_FEATURES.SEGMENTS,
    PLAN_FEATURES.REPORTS,
    PLAN_FEATURES.ANALYTICS,
  ],
  BASIC: [
    PLAN_FEATURES.DASHBOARD,
    PLAN_FEATURES.ONBOARDING,
    PLAN_FEATURES.CUSTOMERS,
    PLAN_FEATURES.ORDERS,
    PLAN_FEATURES.SALE,
    PLAN_FEATURES.REPAIRS,
  ],
  PRO: [
    PLAN_FEATURES.DASHBOARD,
    PLAN_FEATURES.ONBOARDING,
    PLAN_FEATURES.CUSTOMERS,
    PLAN_FEATURES.ORDERS,
    PLAN_FEATURES.SALE,
    PLAN_FEATURES.REPAIRS,
    PLAN_FEATURES.OLD_GOLD,
    PLAN_FEATURES.SEGMENTS,
    PLAN_FEATURES.REPORTS,
  ],
  ENTERPRISE: [
    PLAN_FEATURES.DASHBOARD,
    PLAN_FEATURES.ONBOARDING,
    PLAN_FEATURES.CUSTOMERS,
    PLAN_FEATURES.ORDERS,
    PLAN_FEATURES.SALE,
    PLAN_FEATURES.REPAIRS,
    PLAN_FEATURES.OLD_GOLD,
    PLAN_FEATURES.SEGMENTS,
    PLAN_FEATURES.REPORTS,
    PLAN_FEATURES.ANALYTICS,
    PLAN_FEATURES.PRIORITY_SUPPORT,
  ],
};

/**
 * Live feature overrides. The package catalog is managed from the admin panel,
 * so the feature set per plan is loaded from the API and registered here. Plan
 * ids not present here fall back to the static map above.
 */
const LIVE_MODULES: Record<string, string[]> = {};

export function registerPlanFeatures(planId?: string | null, features?: string[] | null): void {
  if (planId && Array.isArray(features)) {
    LIVE_MODULES[planId] = features;
  }
}

export function getPlanModules(planId?: string | null): string[] {
  const live = planId ? LIVE_MODULES[planId] : undefined;
  if (live) return live;
  return PLAN_MODULES[planId || 'FREE'] || PLAN_MODULES.FREE;
}

export function hasPlanFeature(planId: string | null | undefined, feature: string): boolean {
  return getPlanModules(planId).includes(feature);
}

export const PLAN_NAMES: Record<string, string> = {
  FREE: 'Demo / Free Trial',
  BASIC: 'Basic',
  PRO: 'Pro',
  ENTERPRISE: 'Enterprise',
};

/** Suggest the next plan tier for upgrades */
export const UPGRADE_PATH: Record<string, string> = {
  FREE: 'BASIC',
  BASIC: 'PRO',
  PRO: 'ENTERPRISE',
  ENTERPRISE: '',
};

/** Locked module -> which plan unlocks it (for lock badges) */
export const FEATURE_UNLOCK_PLAN: Record<string, string> = {
  sale: 'BASIC',
  'old-gold': 'PRO',
  segments: 'PRO',
  reports: 'PRO',
  analytics: 'ENTERPRISE',
};